import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivityTickerItem } from './activity-ticker.types';

/**
 * Horizontally scrolling "recent activity" banner for detail Summary tabs.
 *
 * Replaces five copy-pasted implementations (bid / job / jobsite / vendor /
 * customer summary), each of which had the same two defects:
 *
 *  1. The requestAnimationFrame loop ran INSIDE the Angular zone. zone.js patches
 *     requestAnimationFrame, so every frame scheduled a full ApplicationRef.tick().
 *     A change-detection pass on the bid detail route measured 26 ms, so the loop
 *     was asking for ~1.5 s of CD work per second of wall clock and saturating the
 *     main thread for as long as a Summary tab was open.
 *
 *  2. The start guard was not atomic:
 *
 *         if (!this.tickerAnimFrameId) { setTimeout(() => this.startTicker(), 0); }
 *
 *     `tickerAnimFrameId` was only assigned once the queued callback ran, so two
 *     activity responses landing in the same tick both passed the guard and started
 *     two loops sharing one id field. ngOnDestroy cancelled one; the other ran
 *     forever, holding the destroyed component and its DOM.
 *
 * Both are fixed here: the loop is started inside runOutsideAngular and writes the
 * transform straight to the element (no bound property, so no change detection at
 * all), and the frame id is assigned synchronously so the guard actually guards.
 */
@Component({
  selector: 'app-activity-ticker',
  templateUrl: './activity-ticker.component.html',
  styleUrls: ['./activity-ticker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ActivityTickerComponent implements OnChanges, OnDestroy {
  /** Items to scroll. The track renders them twice for a seamless wrap. */
  @Input() items: ActivityTickerItem[] = [];

  /** Idle scroll speed, px per frame. Negative scrolls right-to-left. */
  @Input() baseSpeed = -0.5;

  /** Scrub speed while hovering one of the edge arrows, px per frame. */
  @Input() hoverSpeed = 3;

  @ViewChild('tickerTrack') private track?: ElementRef<HTMLElement>;

  private frameId = 0;
  private position = 0;
  private running = true;
  private hoverDirection: 'left' | 'right' | null = null;
  private destroyed = false;

  constructor(private readonly zone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['items']) {
      return;
    }
    if (this.items?.length) {
      this.start();
    } else {
      this.stop();
      this.position = 0;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stop();
  }

  // ── Hover handling ───────────────────────────────────────────────────────
  // These are ordinary Angular event bindings, so they do trigger change
  // detection — but only on a real user gesture, not 60 times a second.

  onMouseEnter(): void {
    this.running = false;
  }

  onMouseLeave(): void {
    this.hoverDirection = null;
    this.running = true;
  }

  onHoverDirection(direction: 'left' | 'right'): void {
    this.hoverDirection = direction;
  }

  onHoverDirectionLeave(): void {
    this.hoverDirection = null;
  }

  // ── Animation ────────────────────────────────────────────────────────────

  /**
   * `frameId` is assigned synchronously inside runOutsideAngular, so a second
   * call in the same tick sees a non-zero id and bails. That is what makes the
   * guard atomic, and it is why there is no setTimeout here.
   */
  private start(): void {
    if (this.frameId || this.destroyed) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.frameId = requestAnimationFrame(this.tick);
    });
  }

  private stop(): void {
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
  }

  /**
   * Arrow property so the reference stays stable across frames and can be
   * cancelled. Runs entirely outside Angular.
   */
  private tick = (): void => {
    if (this.destroyed) {
      this.frameId = 0;
      return;
    }

    const track = this.track?.nativeElement;
    // The element can be transiently absent (tab switch, @if re-render); keep the
    // loop alive and pick up again on a later frame. Only ngOnDestroy stops it.
    if (track) {
      const trackWidth = track.scrollWidth / 2;
      if (trackWidth > 0) {
        if (this.hoverDirection) {
          this.position += (this.hoverDirection === 'left' ? 1 : -1) * this.hoverSpeed;
        } else if (this.running) {
          this.position += this.baseSpeed;
        }

        if (this.position <= -trackWidth) {
          this.position += trackWidth;
        } else if (this.position > 0) {
          this.position -= trackWidth;
        }

        // Written directly rather than through [style.transform]. A bound
        // property would need change detection on every frame, which is the
        // whole problem this component exists to solve.
        track.style.transform = `translateX(${this.position}px)`;
      }
    }

    this.frameId = requestAnimationFrame(this.tick);
  };
}
